import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ReassignFacilitiesDialog({ user, open, onOpenChange, onDone }) {
  const [count, setCount] = useState(null);
  const [reps, setReps] = useState([]);
  const [target, setTarget] = useState("unassigned");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setTarget("unassigned");
    api.get(`/users/${user.user_id}/facilities-count`).then(({ data }) => setCount(data.facilities)).catch(() => setCount(0));
    api.get("/users").then(({ data }) => setReps(data.filter((u) => u.user_id !== user.user_id && u.active))).catch(() => setReps([]));
  }, [open, user]);

  const confirm = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const new_rep_id = target === "unassigned" ? null : target;
      await api.post(`/users/${user.user_id}/reassign-facilities`, { new_rep_id });
      // Then disable the user
      await api.patch(`/users/${user.user_id}`, { active: false });
      toast.success("User disabled and facilities reassigned");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reassign failed"); }
    finally { setBusy(false); }
  };

  const skipReassign = async () => {
    if (!user) return;
    setBusy(true);
    try {
      await api.patch(`/users/${user.user_id}`, { active: false });
      toast.success("User disabled");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disable failed"); }
    finally { setBusy(false); }
  };

  if (!user) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reassign-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Disable {user.name}?</DialogTitle></DialogHeader>
        {count === null ? (
          <div className="text-sm text-slate-500">Checking their facilities…</div>
        ) : count === 0 ? (
          <div className="text-sm text-slate-600">
            {user.name} currently has no assigned facilities. You can disable safely.
          </div>
        ) : (
          <>
            <div className="text-sm text-slate-700">
              {user.name} is the assigned rep for <b>{count}</b> facility{count === 1 ? "" : "ies"}. Pick who should take them over.
            </div>
            <div>
              <Label>Reassign to</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="reassign-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">— Leave unassigned —</SelectItem>
                  {reps.map((r) => <SelectItem key={r.user_id} value={r.user_id}>{r.name} <span className="text-slate-400 text-xs ml-1">· {r.role}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          {count === 0 ? (
            <Button onClick={skipReassign} disabled={busy} data-testid="reassign-confirm" className="bg-slate-900 hover:bg-slate-800">Disable user</Button>
          ) : (
            <Button onClick={confirm} disabled={busy} data-testid="reassign-confirm" className="bg-slate-900 hover:bg-slate-800">{busy ? "Working…" : "Reassign & disable"}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
